import DOMPurify from 'dompurify';

/**
 * Sanitiser for admin-authored CMS HTML on its way to a player.
 *
 * This is a real XSS boundary, not a precaution. `LegalPage.content` and
 * `News.content` are plain `String` columns with no sanitisation anywhere on
 * the write path — core's CMS controllers store exactly what was posted — and
 * the news article screen already writes `article.content` straight into
 * `innerHTML`. Giving admins a rich-text editor makes that reachable with a
 * crafted paste, so every CMS field rendered as HTML goes through here.
 *
 * The allow-list matches what the Quill toolbar can actually produce, plus
 * images for news. Anything else — script, style, iframe, event handlers,
 * `javascript:` URLs — is dropped by DOMPurify rather than by a regex of our
 * own, which is the one approach that reliably fails closed.
 */
const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 's', 'sub', 'sup',
  'h1', 'h2', 'h3', 'h4',
  'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'hr',
  'a', 'img', 'span', 'div',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
];

const ALLOWED_ATTR = ['href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'colspan', 'rowspan'];

export function sanitizeCmsHtml(html: string): string {
  return DOMPurify.sanitize(html ?? '', {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    // Keep the text of a stripped element rather than deleting it outright,
    // so removing a disallowed wrapper never silently empties a clause.
    KEEP_CONTENT: true,
  });
}

/**
 * Forces every surviving link to open safely.
 *
 * `target="_blank"` without `rel="noopener"` hands the opened tab a
 * `window.opener` handle back into an authenticated session. DOMPurify
 * allows the attribute; it does not add the counterpart.
 */
export function sanitizeCmsHtmlWithSafeLinks(html: string): string {
  const clean = sanitizeCmsHtml(html);
  const doc = new DOMParser().parseFromString(clean, 'text/html');
  doc.querySelectorAll('a[target="_blank"]').forEach((a) => a.setAttribute('rel', 'noopener noreferrer'));
  return doc.body.innerHTML;
}
